



































































































































































































































































































import {
  Component,
  Mixins,
  Watch,
} from 'vue-property-decorator';
import CancelPretensionModal from '@/components/Pretensions/CancelPretensionModal.vue';
import PretensionHistoryDrawer from '@/components/Pretensions/PretensionHistoryDrawer.vue';
import RejectByControllerModal from '@/components/Pretensions/RejectByControllerModal.vue';
import SigningModal from '@/components/Pretensions/SigningModal.vue';
import SignerAuthIndicator from '@/components/Pretensions/SignerAuthIndicator.vue';
import { Pretension, PretensionAccountingSheet, PretensionStatus } from '@/types/Pretension';
import PretensionController from '@/controllers/Pretension';
import { TaskResponse } from '@/classes/task/types';
import {
  isOidcMode,
  ensureSignerSession,
  signingGateAction,
  startSignerLogin,
  readAuthReturnParam,
  clearAuthReturnParam,
} from '@/services/signerSession';
import DocumentsMixin from '@/mixins/Documents';
import UserMixin from '@/mixins/UserMixin';
import PretensionHttpErrorMixin from '@/mixins/PretensionHttpErrorMixin';
import { StartSigningPreviousAttempt } from '@/controllers/PretensionSigning.types';
import { formatDateTimeDdMmYyyyHhMm, mapStatusHistory, PretensionReportTableHistoryRow } from '@/utils/pretensionsReportDisplay';
import { ChangedByDisplay, displayChangedBy, statusReason } from '@/utils/pretensionStatusHistoryDisplay';
import { describeOperationError } from '@/utils/extractApiErrorMessage';

@Component({
  components: {
    CancelPretensionModal,
    PretensionHistoryDrawer,
    RejectByControllerModal,
    SigningModal,
    SignerAuthIndicator,
  },
})
export default class PretensionPage extends Mixins(
  DocumentsMixin,
  UserMixin,
  PretensionHttpErrorMixin,
) {
  private pretension: Pretension = {
    id: 0,
    sequence: '',
    type: '',
    dateFrom: '',
    dateTo: '',
    postDate: '',
    platformName: '',
    supplierLegalName: '',
    status: PretensionStatus.CREATED,
    clients: [],
    documents: [],
    comment: null,
    sentToErpAt: null,
    erpMessageId: null,
  }

  headers = [
    {
      text: 'Клиент',
      value: 'clientName',
    },
    {
      text: 'Центр затрат',
      value: 'costCenterName',
    },
    {
      text: 'Сумма',
      value: 'sum',
    },
  ]

  accountingSheetHeaders = [
    { text: 'Номер листа учёта', value: 'number' },
    { text: 'Учётный период', value: 'period' },
    { text: 'Сумма без НДС', value: 'amount' },
  ]

  private isLoadingPretension = false;

  private isSendingApprovingRequest = false;

  private rejectModalOpen = false;

  private cancelModalOpen = false;

  // T4 (FML-wsj2.3 C-01): isVisibleCredentialsDialog/showPassword/diadocCredentials удалены.
  // Стейт переехал внутрь SigningModal, здесь — только флаг открытия модалки.
  private isSigningModalOpen = false;

  // Часть 2 (FML-khmj, §6): гейт сессии вернул auth_failed — показать индикатор + повтор.
  private signerAuthFailed = false;

  // T4 (FML-wsj2.3): light-polling статуса. На active-статусах раз в 30s
  // тянем pretension/single (silentReload) — это даёт UX-видимость перехода
  // signer_signing_in_progress → supplier_approve_await без ручного refresh.
  private pollingTimer: number | null = null;

  // Отложенный ре-фетч после async reset-to-draft (BPMN пишет status=draft с лагом).
  // Трекаем, чтобы очистить при destroy/deactivate (иначе колбэк дёрнет loadPretension —
  // и $router.replace внутри неё — на уже размонтированном/ушедшем компоненте).
  private resetToDraftTimer: number | null = null;

  private static readonly POLLING_INTERVAL_MS = 30000;

  // Миграция (§8): payment_await ретайрнут; его роль «ожидание оплаты» исполняет
  // accounting_approve_await (polling авто-обновляет gating по sent_to_erp_at + ловит payment_done).
  private static readonly POLLING_ACTIVE_STATUSES: PretensionStatus[] = [
    PretensionStatus.SIGNER_SIGNING_AWAIT,
    PretensionStatus.SIGNER_SIGNING_IN_PROGRESS,
    PretensionStatus.SUPPLIER_SIGNING_AWAIT,
    PretensionStatus.ACCOUNTING_APPROVE_AWAIT,
  ];

  // C-04 fix: первичная загрузка переезжает из created() в mounted() и идёт ПЕРЕД
  // schedulePolling(). Иначе в mounted() this.pretension.status — дефолтный CREATED
  // (из data()), а реальный статус из API ещё не пришёл. В результате schedulePolling()
  // при первом вызове не запускает polling даже для active-статусов.
  async mounted(): Promise<void> {
    await this.loadPretension(this.pretensionId);
    this.schedulePolling();
    // Часть 2 (§6): возврат с OIDC-логина → повтор подписания (после загрузки статуса).
    this.maybeResumeSigningAfterOidcReturn();
  }

  beforeDestroy(): void {
    this.stopPolling();
  }

  // Defensive: если страница окажется внутри <keep-alive> (сейчас в роутере нет, но
  // на случай будущей оптимизации), beforeDestroy не сработает при route-leave.
  // deactivated останавливает polling без destruction компонента.
  deactivated(): void {
    this.stopPolling();
  }

  activated(): void {
    // При возврате на страницу через keep-alive — перезапустить polling
    // (используя текущий статус, который может быть устаревшим).
    this.schedulePolling();
  }

  @Watch('pretension.status')
  private onStatusChanged(): void {
    // Подстраховка: если статус изменился (mutation success / polling-tick) —
    // пересчитать active/inactive. schedulePolling сам сделает stopPolling first (S-01).
    this.schedulePolling();
  }

  private schedulePolling(): void {
    this.stopPolling();
    if (!PretensionPage.POLLING_ACTIVE_STATUSES.includes(this.pretension.status)) return;
    this.pollingTimer = window.setInterval(
      () => { this.loadPretension(this.pretensionId, true); },
      PretensionPage.POLLING_INTERVAL_MS,
    );
  }

  private stopPolling(): void {
    if (this.pollingTimer !== null) {
      window.clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
    if (this.resetToDraftTimer !== null) {
      window.clearTimeout(this.resetToDraftTimer);
      this.resetToDraftTimer = null;
    }
  }

  // §8: «Отправить в ERP» доступна, пока претензия НЕ опубликована (sent_to_erp_at пуст).
  get isSendToErpAction(): boolean {
    return this.pretension.status === PretensionStatus.ACCOUNTING_APPROVE_AWAIT
      && this.isAccountant
      && !this.pretension.sentToErpAt;
  }

  // §8: «Подтвердить оплату» активна ТОЛЬКО после публикации в ERP (sent_to_erp_at проставлен).
  get isConfirmPaymentAction(): boolean {
    return this.pretension.status === PretensionStatus.ACCOUNTING_APPROVE_AWAIT
      && this.isAccountant
      && !!this.pretension.sentToErpAt;
  }

  // §8: на этапе бухгалтерии бухгалтер видит ОБЕ кнопки (ERP + оплата) одновременно.
  // Активность каждой гейтится по sent_to_erp_at (isSendToErpAction / isConfirmPaymentAction):
  // пусто → активна ERP, оплата disabled; проставлен → активна оплата, ERP disabled.
  get isAccountantAccountingStage(): boolean {
    return this.pretension.status === PretensionStatus.ACCOUNTING_APPROVE_AWAIT && this.isAccountant;
  }

  get isControllingAction(): boolean {
    return this.pretension.status === PretensionStatus.CONTROL_AWAIT
        && this.isPretensionController;
  }

  // §6.4: канонический порядок этапов resume (только назад) + лейблы.
  private static readonly RESUME_STAGE_ORDER: ReadonlyArray<{ stage: PretensionStatus; label: string }> = [
    { stage: PretensionStatus.CONTROL_AWAIT, label: 'Вернуть на контроль' },
    { stage: PretensionStatus.SIGNER_SIGNING_AWAIT, label: 'Вернуть на подпись' },
    { stage: PretensionStatus.SUPPLIER_SIGNING_AWAIT, label: 'Вернуть на отправку поставщику' },
    { stage: PretensionStatus.ACCOUNTING_APPROVE_AWAIT, label: 'Вернуть на бухгалтерию' },
  ];

  get isProblemAction(): boolean {
    return this.pretension.status === PretensionStatus.PROBLEM
        && this.isHR;
  }

  // §6.4: resume-кнопки строго по серверному resumableStages (фронт историю не деривит).
  get resumeStageButtons(): Array<{ stage: string; label: string; action: () => Promise<void> }> {
    if (!this.isProblemAction) return [];
    const resumable = this.pretension.resumableStages ?? [];
    return PretensionPage.RESUME_STAGE_ORDER
      .filter((d) => resumable.includes(d.stage))
      .map((d) => ({
        stage: d.stage,
        label: d.label,
        // arrow сохраняет лексический this → dispatchResumeForStage вызывается как метод компонента.
        action: () => this.dispatchResumeForStage(d.stage),
      }));
  }

  // §6.4: диспетчер resume по этапу. control_await → существующий resubmitToControl
  // (со своей обработкой); остальные → единый dispatchResume с соответствующим контроллером.
  async dispatchResumeForStage(stage: string): Promise<void> {
    if (stage === PretensionStatus.CONTROL_AWAIT) {
      await this.resubmitToControl();
      return;
    }
    const callMap: Record<string, () => Promise<TaskResponse>> = {
      [PretensionStatus.SIGNER_SIGNING_AWAIT]: () => PretensionController.resumeToSigning(this.pretensionId),
      [PretensionStatus.SUPPLIER_SIGNING_AWAIT]: () => PretensionController.resumeToSupplier(this.pretensionId),
      [PretensionStatus.ACCOUNTING_APPROVE_AWAIT]: () => PretensionController.resumeToAccounting(this.pretensionId),
    };
    const call = callMap[stage];
    if (call) await this.dispatchResume(call);
  }

  // 11991: причина «Возникла проблема» / «Отозвано» над карточкой. Поставщику причину не показываем.
  get bannerComment(): string | null {
    if (this.isSupplier) return null;
    return statusReason(this.pretension);
  }

  // 11991: «История обработки» — те же строки, что в «Отчёте по претензиям», новые сверху, как у листа.
  get historyRows(): Array<PretensionReportTableHistoryRow & { actor: ChangedByDisplay }> {
    if (this.isSupplier) return [];
    const since = process.env.VUE_APP_PRETENSION_HISTORY_SINCE || null;
    return mapStatusHistory(this.pretension.statusHistory, (k) => String(this.$t(k)))
      .map((h) => ({ ...h, actor: displayChangedBy(h.changedBy, h.changedAtIso, since) }))
      .reverse();
  }

  private get isSignerApproveAction(): boolean {
    return this.pretension.status === PretensionStatus.SIGNER_SIGNING_AWAIT && this.isSigner;
  }

  // Часть 2 (§6): фьюче-флаг DIADOC_AUTH_MODE через сервис (компоненты не ходят в env).
  get oidcMode(): boolean {
    return isOidcMode();
  }

  /**
   * myDSS-ожидание: пока jBPM подписывает облачной DSS, подписант подтверждает в myDSS
   * (push на телефон, без ввода кода). Видно подписанту в oidc-режиме на промежуточном
   * статусе подписания (страница его поллит — POLLING_ACTIVE_STATUSES).
   */
  get isMyDssWaiting(): boolean {
    return this.oidcMode
      && this.isSigner
      && this.pretension.status === PretensionStatus.SIGNER_SIGNING_IN_PROGRESS;
  }

  get pretensionId(): number {
    return Number(this.$route.params.id);
  }

  // §9: статусы, на которых разрешена явная отмена (recalled достижим только так).
  private static readonly HR_CANCELLABLE: PretensionStatus[] = [
    PretensionStatus.CONTROL_AWAIT,
    PretensionStatus.SIGNER_SIGNING_AWAIT,
    PretensionStatus.SIGNER_SIGNING_IN_PROGRESS,
    PretensionStatus.DIADOC_SIGNING_AWAIT,
    PretensionStatus.SUPPLIER_SIGNING_AWAIT,
    PretensionStatus.ACCOUNTING_APPROVE_AWAIT,
    PretensionStatus.PROBLEM,
  ];

  private static readonly SIGNER_CANCELLABLE: PretensionStatus[] = [
    PretensionStatus.SIGNER_SIGNING_AWAIT,
    PretensionStatus.SIGNER_SIGNING_IN_PROGRESS,
    PretensionStatus.DIADOC_SIGNING_AWAIT,
  ];

  get cancelAllowed(): boolean {
    const status = this.pretension.status;
    return (this.isHR && PretensionPage.HR_CANCELLABLE.includes(status))
      || (this.isSigner && PretensionPage.SIGNER_CANCELLABLE.includes(status))
      || (this.isAccountant && status === PretensionStatus.ACCOUNTING_APPROVE_AWAIT);
  }

  get accountingSheetRows(): Array<PretensionAccountingSheet & { period: string }> {
    return (this.pretension.accountingSheets || []).map((sheet) => ({
      ...sheet,
      period: `${sheet.periodFrom || ''} - ${sheet.periodTo || ''}`,
    }));
  }

  get displayedHeaders(): Array<{ text: string; value: string }> {
    if (!this.isSupplier) return this.headers;
    // БЦ3.3 followup Issue 1.2: SUPPLIER не видит internal FML breakdown колонки:
    //   - clientName: internal FML client (PHILIPS и т.п.)
    //   - costCenterName: internal FML cost center
    // Остаётся только Сумма (supplier видит итоговые суммы по строкам breakdown).
    const hidden: string[] = ['clientName', 'costCenterName'];
    return this.headers.filter((h) => !hidden.includes(h.value));
  }

  // BC2.1' (FML-svpt): форматирование sentToErpAt для отображения в шапке карточки.
  // Источник формата — `formatDateTimeDdMmYyyyHhMm` (dd.mm.yyyy HH:MM, единый для всех
  // pretension-отображений). Поле опциональное (заполняется PHP после ActiveMQ publish),
  // защита через ?? null для strict TS.
  get sentToErpAtFormatted(): string {
    return formatDateTimeDdMmYyyyHhMm(this.pretension.sentToErpAt ?? null);
  }

  // Свой ключ у diadoc_signing_await: голый ключ в локали занят подписью листа учёта.
  humanizeStatus(code: string): string {
    const key = code === PretensionStatus.DIADOC_SIGNING_AWAIT ? 'pretension_status_diadoc_signing_await' : code;
    return String(this.$t(key));
  }

  public getDocumentType(type: string): string {
    const map = new Map([
      ['pretension', 'Претензия'],
      ['attachment', 'Приложение'],
      ['default', 'Приложение'],
    ]);
    const name = map.get(type) !== undefined ? map.get(type) : map.get('default');
    return name || '';
  }

  // T4 (FML-wsj2.3): silentReload=true — никаких UI side-effects (spinner, notifications,
  // router-навигации). Polling-tick не должен уводить пользователя со страницы.
  async loadPretension(id: number, silentReload = false): Promise<void> {
    if (!id) {
      return;
    }
    if (!silentReload) {
      this.isLoadingPretension = true;
    }
    try {
      // Каждая загрузка карточки, и опрос тоже, отмечает претензию прочитанной: смену статуса
      // на открытой карточке человек видел (задача 10833).
      const response = await PretensionController.single(id, true);
      const [error, result] = response.result;

      if (error) {
        // BC3.3 (S2 fix): развести access-denied (401/403/404) от сетевых ошибок (5xx/timeout).
        // ESB Talend Job 1.2 под D7=c возвращает 404 для SUPPLIER на чужую претензию.
        const code = response.code;
        const isAccessDenied = code === 401 || code === 403 || code === 404;
        if (this.isSupplier && isAccessDenied) {
          if (silentReload) {
            console.error('[pretension polling] access denied during silent reload');
            return;
          }
          this.$router.replace({ name: 'AccessDenied' });
          return;
        }
        if (silentReload) {
          console.error('[pretension polling] load error:', error);
          return;
        }
        this.$bus.$emit('notification', 'Возникла ошибка при получении претензии, попробуйте позже', 'error');
        console.error(error);
        return;
      }

      // BC3.3 N2 (defense-in-depth): нормализация на случай, если Talend Job когда-нибудь
      // переключится на D7=a path (single через pretension-list?id=X — массив).
      const data = Array.isArray(result?.result) ? result.result[0] : result?.result;

      if (!data) {
        // ESB ACL-гейт под D7=c вернул 404 без body для SUPPLIER на чужой id,
        // либо реально несуществующая претензия для любой роли.
        if (this.isSupplier) {
          if (silentReload) {
            console.error('[pretension polling] not found / supplier — silent');
            return;
          }
          this.$router.replace({ name: 'AccessDenied' });
          return;
        }
        if (silentReload) {
          console.warn('[pretension polling] not found during silent reload');
          return;
        }
        // N3 fix: для HR — notification + go back. Без этого остаётся дефолтный
        // pretension-объект в data() и v-card-actions v-if="pretension" продолжает
        // рендериться с возможно зажжёнными action-кнопками на статусе CREATED.
        this.$bus.$emit('notification', 'Претензия не найдена', 'warning');
        this.$router.go(-1);
        return;
      }

      // BC3.3 rev.9 FR-FE-016: id-mismatch defense-in-depth поверх backend ACL.
      // ESB Single 1.2 использует globalMap pattern для userSupplierId — в Spring Boot
      // request pool возможна race между concurrent SUPPLIER-запросами, backend может
      // вернуть pretension с id != запрошенного. Не security-граница (primary —
      // ESB AC-ESB-CONCURRENT), а sanity check на consistency ответа. D8-compliant:
      // сравниваем id, не supplier_id.
      if (data.id !== id) {
        console.error(`[loadPretension] requested id=${id}, received id=${data.id} — refusing to render`);
        if (silentReload) return; // silent: только лог, без router-навигации и notification
        if (this.isSupplier) {
          this.$router.replace({ name: 'AccessDenied' });
          return;
        }
        this.$bus.$emit('notification', 'Сервис вернул некорректные данные, попробуйте позже', 'error');
        this.$router.go(-1);
        return;
      }

      // §6.4: бэк отдаёт resumableStages (camelCase, как sentToErpAt). Защита от snake/отсутствия.
      data.resumableStages = (data.resumableStages ?? (data as any).resumable_stages ?? []) as string[];
      this.pretension = data;
      // BC3.3 followup (rev.4/5): makeDocumentsLinksForPretension теперь sync (sync URL builder),
      // никакого await. Frontend получает stable URL'ы за O(1) без N RTT к backend.
      this.makeDocumentsLinksForPretension(data);
    } catch (e) {
      console.error(e);
      if (!silentReload) {
        this.$bus.$emit('notification', 'Возникла ошибка при получении претензии, попробуйте позже', 'error');
      }
    } finally {
      if (!silentReload) {
        this.isLoadingPretension = false;
      }
    }
  }

  onSubmit(): void {
    if (this.isSignerApproveAction) {
      // Часть 2 (§6): перед инициацией подписания — гейт сессии подписанта (oidc).
      // Открытие модалки происходит внутри initiateSigning только при валидной сессии.
      this.initiateSigning();
    } else {
      this.$bus.$emit('notification', 'Невозможно сопоставить статус претензии и роль пользователя для определения действия', 'warning');
    }
  }

  /**
   * Часть 2 (§6): гейт сессии подписанта перед инициацией подписания.
   * legacy → сразу модалка (логин/пароль внутри). oidc → ensureSignerSession:
   *   proceed → открыть модалку (oidc-режим: без формы, авто-старт + myDSS);
   *   show_auth_failed → индикатор «не удалось» + кнопка повтора;
   *   redirecting → браузер уходит на OIDC-логин, ничего не делаем.
   * signerEmail = email залогиненного подписанта (identity для сессии/myDSS, не authz).
   */
  async initiateSigning(): Promise<void> {
    this.signerAuthFailed = false;
    // Оптимистичный disable кнопки (:loading) на время гейта — сужает окно ре-клика
    // в network-раунде ensureSignerSession. Идемпотентность дублей — за бэк-замком
    // /pretension-start-signing (авторитетный guard: гонки/ретраи/мульти-клиент).
    this.isSendingApprovingRequest = true;
    try {
      if (!this.oidcMode) {
        this.isSigningModalOpen = true;
        return;
      }
      const result = await ensureSignerSession(this.pimcoreUser?.email);
      const action = signingGateAction(result);
      if (action === 'proceed') this.isSigningModalOpen = true;
      else if (action === 'show_auth_failed') this.signerAuthFailed = true;
      // redirecting → no-op
    } finally {
      this.isSendingApprovingRequest = false;
    }
  }

  /** Повтор входа из индикатора: полный редирект на OIDC-логин подписанта. */
  onSignerAuthRetry(): void {
    startSignerLogin(this.pimcoreUser?.email);
  }

  /**
   * После возврата с OIDC-логина (?auth=ok|fail) — повтор исходного действия (Часть 1 §5.2).
   * Только для подписанта в статусе подписания; иначе чистим параметр, чтобы refresh
   * не реплеил param-логику. ensureSignerSession внутри initiateSigning сам потребит ?auth.
   */
  maybeResumeSigningAfterOidcReturn(): void {
    if (!this.oidcMode) return;
    if (readAuthReturnParam() === null) return;
    if (this.isSignerApproveAction) this.initiateSigning();
    else clearAuthReturnParam();
  }

  onError(error: any): void {
    this.$bus.$emit('notification', `Возникла ошибка при выполнении операции: ${describeOperationError(error)}`, 'error');
    console.error(error);
  }

  onSuccess(result: any): void {
    console.log('Result after request - ', result);
    this.$bus.$emit('notification', 'Операция успешно выполнена', 'success');
    this.loadPretension(this.pretensionId);
  }

  // T4 (FML-wsj2.3): handlers для SigningModal. Сам сабмит и handleResponse — внутри модалки.
  onSigningSuccess(): void {
    // Пользователь увидел State 3a и нажал ОК (или закрыл X из SUCCESS, I-05).
    // Immediate refresh: увидим signer_signing_in_progress на карточке, дальше polling
    // подхватит переход в supplier_approve_await (см. polling task).
    this.loadPretension(this.pretensionId);
  }

  onSigningRecovered(previousAttempt: StartSigningPreviousAttempt): void {
    // 200 + recovered=true: info-toast (R-T4-08: тостер не поддерживает $t напрямую,
    // строим string через this.$t(...) as string).
    const startedAt = new Date(previousAttempt.lockedAt).toLocaleTimeString('ru-RU', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Moscow',
    });
    this.$bus.$emit(
      'notification',
      this.$t('pretension_signing_toast_recovered', { signerEmail: previousAttempt.signerEmail, startedAt }) as string,
      'info',
    );
  }

  // §8: «Отправить в ERP» — шлёт сигнал accountant_send_to_erp. Статус не меняется
  // (in-place, jBPM проставит sent_to_erp_at). BUG-7: блок «Данные для 1С ERP» (SapData)
  // убран из UI (ERP принимает другой набор данных) — публикация идёт без 1С-данных.
  async submitSendToErp(): Promise<void> {
    // Флаг ставим ПЕРВЫМ (до await) — кнопка дизейблится по isSendingApprovingRequest,
    // сужает окно ре-клика. finally сбрасывает на всех путях.
    this.isSendingApprovingRequest = true;
    try {
      const [error, result] = (await PretensionController.sendToErp(this.pretensionId)).result;
      if (error) this.onError(error);
      else this.onSuccess(result);
    } catch (error) {
      this.onError(error);
    } finally {
      this.isSendingApprovingRequest = false;
    }
  }

  async sendPaymentDone(): Promise<void> {
    try {
      this.isSendingApprovingRequest = true;
      const [error, result] = (await PretensionController.paymentDone(this.pretensionId)).result;
      if (error) this.onError(error);
      else this.onSuccess(result);
    } catch (error) {
      this.onError(error);
    } finally {
      this.isSendingApprovingRequest = false;
    }
  }

  async approveByControlling(): Promise<void> {
    this.isSendingApprovingRequest = true;
    try {
      const response = await PretensionController.approveByControlling(this.pretensionId);
      if (response.result[0]) {
        const outcome = this.classifyHttpError(response);
        this.$bus.$emit('notification', outcome.message, outcome.type);
        if (outcome.shouldReloadPretension) {
          await this.loadPretension(this.pretensionId);
        }
        return;
      }
      this.$bus.$emit('notification', 'Операция успешно выполнена', 'success');
      await this.loadPretension(this.pretensionId);
    } catch (error) {
      this.$bus.$emit('notification', `Возникла ошибка — ${error}`, 'error');
    } finally {
      this.isSendingApprovingRequest = false;
    }
  }

  /**
   * п.4: «Вернуть в черновик» из статуса problem. АСИНХРОНЕН (FML-wamh): 200 = «сигнал принят»,
   * статус в ответе ещё problem; запись status=draft делает BPMN позже (сигнал→scriptTask→terminate).
   * Не трактуем ответ как «уже черновик», не пускаем в edit — сообщаем про лаг и перечитываем
   * (сразу + с задержкой, чтобы поймать запись BPMN).
   */
  async resetToDraft(): Promise<void> {
    this.isSendingApprovingRequest = true;
    try {
      const response = await PretensionController.resetToDraft(this.pretensionId);
      if (response.code === 409) {
        this.$bus.$emit('notification', 'Возврат в черновик доступен только из статуса «Возникла проблема»', 'warning');
        await this.loadPretension(this.pretensionId);
        return;
      }
      if (response.code === 503) {
        this.$bus.$emit('notification', 'Сервис временно недоступен, повторите попытку позже', 'error');
        return;
      }
      if (response.result[0]) {
        const outcome = this.classifyHttpError(response);
        this.$bus.$emit('notification', outcome.message, outcome.type);
        if (outcome.shouldReloadPretension) {
          await this.loadPretension(this.pretensionId);
        }
        return;
      }
      this.$bus.$emit('notification', 'Запрошен возврат в черновик. Статус обновится через несколько секунд', 'success');
      await this.loadPretension(this.pretensionId);
      if (this.resetToDraftTimer !== null) {
        window.clearTimeout(this.resetToDraftTimer);
      }
      this.resetToDraftTimer = window.setTimeout(() => {
        this.resetToDraftTimer = null;
        this.loadPretension(this.pretensionId);
      }, 4000);
    } catch (error) {
      this.$bus.$emit('notification', `Возникла ошибка — ${error}`, 'error');
    } finally {
      this.isSendingApprovingRequest = false;
    }
  }

  // DRY: единый обработчик resume-сигнала (зеркалит resubmitToControl).
  private async dispatchResume(call: () => Promise<TaskResponse>): Promise<void> {
    this.isSendingApprovingRequest = true;
    try {
      const response = await call();
      if (response.result[0]) {
        const outcome = this.classifyHttpError(response);
        this.$bus.$emit('notification', outcome.message, outcome.type);
        if (outcome.shouldReloadPretension) await this.loadPretension(this.pretensionId);
        return;
      }
      this.$bus.$emit('notification', 'Операция успешно выполнена', 'success');
      await this.loadPretension(this.pretensionId);
    } catch (error) {
      this.$bus.$emit('notification', `Возникла ошибка — ${error}`, 'error');
    } finally {
      this.isSendingApprovingRequest = false;
    }
  }

  async resubmitToControl(): Promise<void> {
    this.isSendingApprovingRequest = true;
    try {
      const response = await PretensionController.controlResubmit(this.pretensionId);
      if (response.result[0]) {
        const outcome = this.classifyHttpError(response);
        this.$bus.$emit('notification', outcome.message, outcome.type);
        if (outcome.shouldReloadPretension) {
          await this.loadPretension(this.pretensionId);
        }
        return;
      }
      this.$bus.$emit('notification', 'Операция успешно выполнена', 'success');
      await this.loadPretension(this.pretensionId);
    } catch (error) {
      this.$bus.$emit('notification', `Возникла ошибка — ${error}`, 'error');
    } finally {
      this.isSendingApprovingRequest = false;
    }
  }
}
